import { Accordion } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CustomFieldEntityEnum,
  ILegalEntityRequestSchema,
  ILegalEntityResponseSchema,
  LegalEntityAddressFormatEnum,
  LegalEntityRequestSchema,
} from '@monetize/types/app';
import { nullifyEmptyStrings } from '@monetize/utils/core';
import { AxiosHeaders } from 'axios';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { handleApiErrorToast } from '../../../api/axios';
import {
  useCreateEntity,
  useGetById,
  useUpdateEntity,
} from '../../../api/queryUtils';
import { AddressForm } from '../../../components/Contacts/AddressForm';
import { CustomFieldDataForm } from '../../../components/CustomFields/CustomFieldDataForm';
import {
  MAlert,
  MBox,
  MButton,
  MFormField,
  MGrid,
  MGridItem,
  MInput,
  MPageContainer,
  MPageLoader,
  MSettingsPageHeader,
  MVStack,
} from '../../../components/Monetize';
import { MCustomSelect } from '../../../components/Monetize/MCustomSelect/MCustomSelect';
import { MSettingAccordionItem } from '../../../components/Monetize/MSettingAccordionItem';
import { LEGAL_ENTITIES } from '../../../constants';
import { SETTINGS_CONTENT_MAX_WIDTH } from '../../../constants/settings';
import { useLegalEntityLogo } from '../../../hooks/useLegalEntityLogo';
import {
  BLANK_TEMPLATE_VALUE,
  useQuoteTemplatesList,
} from '../../../hooks/useQuoteTemplatesList';
import { usePrompt } from '../../../hooks/usePrompt';
import { useSettingsReadOnlyRule } from '../../../hooks/useSettingsReadOnlyRule';
import { getCountryCodeFromNameOrCode } from '../../../services/countryState';
import { useDocumentHead } from '../../../services/documentHead';
import { useFlags } from '../../../services/launchDarkly';
import { logger } from '../../../services/logger';
import { dataUrlToFile } from '../../../utils/download';
import { LegalEntityLogo } from './LegalEntityLogo';
import { LegalEntityInvoiceTerms } from './components/LegalEntityInvoiceTerms';
import { LegalEntityLogoDropzone } from './components/LegalEntityLogoDropzone';
import { LegalEntityQuoteDocuments } from './components/LegalEntityQuoteDocuments';

export const LegalEntityForm = () => {
  const { enableLegalEntityInvoiceTerms, enableQuoteDocumentManagement } =
    useFlags();
  const { legalEntityId } = useParams();
  const { setDocTitle } = useDocumentHead();
  const navigate = useNavigate();
  const { quoteTemplates } = useQuoteTemplatesList(true);

  const { data: legalEntityData, isLoading: isLegalEntityDataLoading } =
    useGetById<ILegalEntityResponseSchema>(
      'legalEntities',
      legalEntityId ?? '',
      {
        enabled: !!legalEntityId,
        refetchOnWindowFocus: false,
      },
    );

  const {
    mutateAsync: createLegalEntity,
    isPending: isCreateLegalEntityLoading,
  } = useCreateEntity<ILegalEntityResponseSchema, FormData>('legalEntities', {
    onError: (err) => {
      handleApiErrorToast(err);
    },
    axiosRequestConfig: {
      headers: new AxiosHeaders({
        'Content-Type': 'multipart/form-data',
      }),
    },
  });

  const { isLoading: isLogoLoading, base64: base64String } = useLegalEntityLogo(
    { id: legalEntityId },
  );

  const {
    mutateAsync: updateLegalEntity,
    isPending: isUpdateLegalEntityLoading,
    isSuccess: isFormUpdateSuccess,
  } = useUpdateEntity<ILegalEntityResponseSchema, ILegalEntityRequestSchema>(
    'legalEntities',
    { meta: { showErrorToast: true } },
  );

  const defaultValues = {
    file: base64String ? dataUrlToFile(base64String, 'logo') : undefined,
    name: legalEntityData?.name ?? '',
    customId: legalEntityData?.customId ?? '',
    email: legalEntityData?.email,
    vatNumber: legalEntityData?.vatNumber ?? '',
    phoneNumber: legalEntityData?.phoneNumber ?? '',
    line1: legalEntityData?.line1 ?? '',
    line2: legalEntityData?.line2 ?? '',
    city: legalEntityData?.city ?? '',
    state: legalEntityData?.state ?? '',
    postalCode: legalEntityData?.postalCode ?? '',
    country: getCountryCodeFromNameOrCode(legalEntityData?.country) ?? '',
    addressFormat:
      legalEntityData?.addressFormat ?? LegalEntityAddressFormatEnum.US_FORMAT,
    quoteTemplate: legalEntityData?.quoteTemplate ?? BLANK_TEMPLATE_VALUE,
    customFields: legalEntityData?.customFields || {},
    options: legalEntityData?.options || {
      invoiceTerms: {
        active: false,
        currencyTerms: [],
      },
    },
  };

  const formMethods = useForm<ILegalEntityRequestSchema>({
    resolver: zodResolver(LegalEntityRequestSchema),
    mode: 'onSubmit',
    defaultValues,
    values: defaultValues,
    resetOptions: {
      keepDirtyValues: false,
      keepErrors: false,
    },
    shouldFocusError: false,
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    getValues,
    reset,
    watch,
  } = formMethods;

  const onError = (errs: any, event: any) => {
    logger.warn(`ERRORS:`, errs);
  };

  const onSubmit = async ({
    file,
    ...finalData
  }: ILegalEntityRequestSchema) => {
    finalData.quoteTemplate =
      finalData.quoteTemplate === BLANK_TEMPLATE_VALUE
        ? null
        : finalData.quoteTemplate;

    if (legalEntityId) {
      updateLegalEntity({ id: legalEntityId, payload: finalData });
      return;
    }

    const formData = new FormData();

    if (file) {
      formData.append('file', file);
    }
    formData.append(
      'data',
      new Blob([JSON.stringify(nullifyEmptyStrings(finalData))], {
        type: 'application/json',
      }),
    );

    try {
      await createLegalEntity(formData);
    } catch {
      // createLegalEntity's onError surfaces the toast. Leave the form dirty
      // so the unsaved-changes guard stays armed for the failed save.
      return;
    }

    // Mark the saved values as the new baseline before leaving. usePrompt
    // installs a history blocker and navigate(-1) is a POP, which the router
    // only evaluates a task later - a form left dirty here would re-arm the
    // blocker and prompt the user to discard the changes they just saved.
    reset(getValues());
    navigate(-1);
  };

  useEffect(() => {
    setDocTitle('Settings', `Legal Entities`);
  }, []);

  const isSaving = isCreateLegalEntityLoading || isUpdateLegalEntityLoading;

  const { isReadOnly, inputVariant } = useSettingsReadOnlyRule();

  usePrompt(
    'You have unsaved changes. Are you sure you want to leave without saving?',
    isDirty && !isReadOnly && !isSaving,
  );

  if (!!legalEntityId && (isLegalEntityDataLoading || isLogoLoading)) {
    return <MPageLoader />;
  }

  return (
    <MPageContainer alignItems="stretch" data-testid="companySettings-form">
      <MBox position="sticky" top="0" zIndex={1} bg="white" pt={2}>
        <MSettingsPageHeader
          hasBackButton
          title={!legalEntityId ? 'New Entity' : 'Edit Entity'}
        >
          {!isReadOnly && (
            <>
              {isDirty &&
                !isUpdateLegalEntityLoading &&
                !isCreateLegalEntityLoading && (
                  <MAlert
                    type="warning"
                    alertProps={{ px: 2, py: 1 }}
                    alertIconProps={{ width: 4, height: 4 }}
                    alertDescriptionProps={{ fontSize: 'sm' }}
                    mr="4"
                  >
                    Unsaved Changes
                  </MAlert>
                )}
              <MButton
                type="submit"
                variant="primary"
                isLoading={isSaving}
                isDisabled={
                  !isDirty ||
                  isCreateLegalEntityLoading ||
                  isUpdateLegalEntityLoading
                }
                onClick={handleSubmit(onSubmit, onError)}
              >
                Save
              </MButton>
            </>
          )}
        </MSettingsPageHeader>
      </MBox>

      <MBox>
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <MGrid
            templateColumns={{
              base: '1fr',
              '2xl': 'repeat(2, minmax(0, 1fr))',
            }}
            columnGap={8}
            rowGap={4}
            mb={4}
            maxW={SETTINGS_CONTENT_MAX_WIDTH}
          >
            <MGridItem colSpan={{ base: 1, '2xl': 2 }}>
              {!legalEntityId && (
                <MFormField error={errors.file} label="Upload Logo">
                  <Controller
                    name="file"
                    control={control}
                    render={({ field: { value, onChange, ...rest } }) => (
                      <LegalEntityLogoDropzone
                        value={value}
                        onChange={onChange}
                        {...rest}
                      />
                    )}
                  />
                </MFormField>
              )}
              {!!legalEntityId && (
                <LegalEntityLogo
                  isReadOnly={isReadOnly}
                  legalEntityId={legalEntityId}
                  resetErrorState={isFormUpdateSuccess}
                />
              )}
            </MGridItem>

            <MGridItem>
              <MVStack align="stretch" gap={4}>
                <MFormField error={errors.name} label="Company Name" isRequired>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MInput
                        maxLength={120}
                        value={value || ''}
                        placeholder="Enter Company Name"
                        {...rest}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                      />
                    )}
                  />
                </MFormField>

                <MFormField error={errors.customId} label="Custom ID">
                  <Controller
                    name="customId"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MInput
                        value={value || ''}
                        placeholder="Enter Custom ID"
                        {...rest}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                      />
                    )}
                  />
                </MFormField>

                <MFormField error={errors.email} label="Email">
                  <Controller
                    name="email"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MInput
                        value={value || ''}
                        placeholder="Enter Email"
                        {...rest}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                      />
                    )}
                  />
                </MFormField>

                <MFormField error={errors.phoneNumber} label="Phone Number">
                  <Controller
                    name="phoneNumber"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MInput
                        value={value || ''}
                        placeholder="Enter Phone Number"
                        {...rest}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                      />
                    )}
                  />
                </MFormField>

                <MFormField error={errors.vatNumber} label="VAT Number">
                  <Controller
                    name="vatNumber"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MInput
                        value={value || ''}
                        placeholder="Enter VAT Number"
                        {...rest}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                      />
                    )}
                  />
                </MFormField>
              </MVStack>
            </MGridItem>

            <MGridItem>
              <MVStack align="stretch" gap={4}>
                <MGrid templateColumns="repeat(2, 1fr)" columnGap={4}>
                  <MGridItem>
                    <MFormField
                      error={errors.addressFormat}
                      label="Address Format"
                      isRequired
                    >
                      <Controller
                        name="addressFormat"
                        control={control}
                        render={({ field }) => (
                          <MCustomSelect
                            items={LEGAL_ENTITIES.LEGAL_ENTITY_ADDRESS_FORMATS}
                            isReadOnly={isReadOnly}
                            variant={inputVariant}
                            {...field}
                          />
                        )}
                      />
                    </MFormField>
                  </MGridItem>

                  <MGridItem>
                    <MFormField label="Quote Template">
                      <Controller
                        name="quoteTemplate"
                        control={control}
                        render={({ field: { ...rest } }) => (
                          <MCustomSelect items={quoteTemplates} {...rest} />
                        )}
                      />
                    </MFormField>
                  </MGridItem>
                </MGrid>

                <FormProvider {...formMethods}>
                  <AddressForm
                    isDisabled={isReadOnly}
                    isReadOnly={isReadOnly}
                    formKeyPrefix=""
                  />
                </FormProvider>
              </MVStack>
            </MGridItem>

            <MGridItem colSpan={{ base: 1, '2xl': 2 }}>
              <FormProvider {...formMethods}>
                <Controller
                  name="customFields"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <CustomFieldDataForm
                      accordionAutoExpanded
                      entity={CustomFieldEntityEnum.LEGAL_ENTITY}
                      value={value || {}}
                      setValue={onChange}
                      fieldColSpan={4}
                      mode="accordion"
                      isReadOnly={isReadOnly}
                      showDivider
                    />
                  )}
                />
              </FormProvider>
            </MGridItem>
          </MGrid>
        </form>
      </MBox>

      {enableLegalEntityInvoiceTerms && (
        <MBox mb={4} maxW={SETTINGS_CONTENT_MAX_WIDTH}>
          <Accordion.Root multiple w="full">
            <MSettingAccordionItem
              label="Invoice Terms"
              description="You can override the invoice terms from 'Invoice Settings' for this Legal Entity and define different terms for each currency."
            >
              <LegalEntityInvoiceTerms
                control={control}
                errors={errors}
                watch={watch}
                isReadOnly={isReadOnly}
              />
            </MSettingAccordionItem>
          </Accordion.Root>
        </MBox>
      )}

      {legalEntityId && enableQuoteDocumentManagement && (
        <MBox mb={4} maxW={SETTINGS_CONTENT_MAX_WIDTH}>
          <Accordion.Root multiple w="full">
            <MSettingAccordionItem label="Quote Documents">
              <LegalEntityQuoteDocuments legalEntityId={legalEntityId} />
            </MSettingAccordionItem>
          </Accordion.Root>
        </MBox>
      )}
    </MPageContainer>
  );
};
